// @ts-nocheck
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import { DataTable } from '@/components/organism/Table';
import PageToolbar from '@/components/organism/Cards/PageToolbar';
import FullPageCard from '@/components/organism/Cards/FullPageCard';
import Autocomplete from '@/components/elements/AutoComplete';
import { useDateFilter } from '@/context/dateFilter.context';
import {
  ROW_PER_PAGE_OPTION,
  STRINGS,
  pagination as PAGINATION_INITIAL_STATE,
  SortEnum,
} from '@/constant/global/global.constant';
import { getCronJobTrackerDataDetails } from '@/store/selectors/cronJobsTracker.selector';
import { getCronJobsTrackerData } from '@/store/slices/cronJobsTracker.slice';
import {
  CRON_JOBS_TRACKER_UI_PROPS,
  ECronJobNameInterface,
  ECronJobTypeDropdownOptionsList,
} from '@/services/cronJobsTracker/cronJobsTrackerConstants';
import { CronJobExpandedPanel } from '@/components/organism/TableExpand/CronJobExpanded/CronJobExpandedPanel';
import { NAV_ITEMS, getNavItemIcon } from '@/constant/layout/layout.constant';
import { tid } from '@/utils/testId';
import { CRON_JOBS_TEST_IDS as TID } from '@/constant/testIds/cronJobs.testIds';
import { ICronJobTrackerProps } from './cronJobsTracker.interface';
import { buildColumns } from './cronJobsTracker.columns';

/* ── Page ─────────────────────────────────────────────────────────────────── */
const CronJobsTracker = () => {
  const dispatch = useDispatch();
  const { filterValues } = useDateFilter();
  const { startDate, endDate } = filterValues;
  const hasLoaded = useRef(false);

  const isCopyRef = useRef('');
  const [isCopy, setIsCopy] = useState('');
  const [expandedRowIndex, setExpandedRowIndex] = useState<number | null>(null);
  const [selectedJobName, setSelectedJobName] = useState(
    ECronJobTypeDropdownOptionsList[0],
  );
  const [sorting, setSorting] = useState({
    orderBy: 'createdOn',
    order: 'desc' as SortEnum,
  });
  const [pagination, setPagination] = useState(PAGINATION_INITIAL_STATE);

  const {
    loading,
    cronJobsTrackerData: { data, pagination: paginationResponse },
  } = useSelector(getCronJobTrackerDataDetails);

  // Track first successful data load
  useEffect(() => {
    if (!hasLoaded.current && Array.isArray(data) && data.length > 0) {
      hasLoaded.current = true;
    }
  }, [data]);

  const totalPages = Math.ceil(
    (paginationResponse?.totalRecords || 0) / pagination.limit,
  );

  const onCopyClick = useCallback((key: string) => {
    setIsCopy(key);
    setTimeout(() => {
      setIsCopy('');
    }, 500);
  }, []);

  const handlePageSizeChange = useCallback(
    (value: number) => {
      setPagination((p) => ({
        ...p,
        limit: value,
        page: totalPages < p.page ? totalPages : p.page,
      }));
    },
    [totalPages],
  );

  const handlePreviousClick = useCallback((page: number) => {
    if (page > 1) setPagination((p) => ({ ...p, page: page - 1 }));
  }, []);

  const handleNextClick = useCallback(
    (page: number) => {
      if (page < totalPages) setPagination((p) => ({ ...p, page: page + 1 }));
    },
    [totalPages],
  );

  useEffect(() => {
    if (pagination?.page > totalPages && totalPages !== 0) {
      setPagination((p) => ({ ...p, page: 1 }));
    }
  }, [totalPages, pagination?.page]);

  useEffect(() => {
    dispatch(
      getCronJobsTrackerData({
        ...pagination,
        ...sorting,
        jobName: selectedJobName?.value,
        startDate,
        endDate,
      }),
    );
  }, [
    pagination.limit,
    pagination.page,
    sorting.orderBy,
    sorting.order,
    selectedJobName?.value,
    startDate,
    endDate,
  ]);

  const columns = useMemo(
    () => buildColumns(isCopy, onCopyClick),
    [isCopy, onCopyClick],
  );

  return (
    <FullPageCard
      testId={tid(TID.PAGE)}
      className="rounded-b-none"
      header={
        <PageToolbar
          title={STRINGS.CRON_JOBS_TRACKER}
          subtitle={STRINGS.CRON_JOBS_TRACKER_SUB}
          icon={getNavItemIcon(NAV_ITEMS.CRONJOBS)}
          filtersLeft={
            <div className="w-[220px]">
              <Autocomplete
                label={CRON_JOBS_TRACKER_UI_PROPS.JOB_NAME_DROPDOWN.LABEL}
                placeholder={
                  CRON_JOBS_TRACKER_UI_PROPS.JOB_NAME_DROPDOWN.PLACEHOLDER
                }
                dataTestId={tid(TID.JOB_NAME_SELECT)}
                selectedValue={selectedJobName}
                handleSelectedValue={(v: any) => {
                  setSelectedJobName(v ?? ECronJobTypeDropdownOptionsList[0]);
                  setPagination((p) => ({ ...p, page: 1 }));
                }}
                lists={ECronJobTypeDropdownOptionsList}
                nameKey="jobName"
              />
            </div>
          }
        />
      }
    >
      <DataTable
        data-testid={tid(TID.TABLE)}
        data={data ?? []}
        columns={columns}
        loading={loading}
        skeletonRows={15}
        onSort={(orderBy, order) =>
          setSorting({ orderBy, order: order as SortEnum })
        }
        sortState={sorting}
        rowKey={(item: any) => item?.cronJobId ?? ''}
        rowTestId={(item: any, idx: number) =>
          TID.TABLE_ROW(`${idx}`)
        }
        pagination={
          !isEmpty(data)
            ? {
                state: pagination,
                totalItem: paginationResponse?.totalRecords,
                rowsPerPageOptions: ROW_PER_PAGE_OPTION,
                onRowsPerPageChange: handlePageSizeChange,
                onNextClick: handleNextClick,
                onPreviousClick: handlePreviousClick,
              }
            : null
        }
        expandedRowIndex={expandedRowIndex}
        onExpandChange={(idx) =>
          setExpandedRowIndex(idx === expandedRowIndex ? null : idx)
        }
        renderExpandedRow={(row) => <CronJobExpandedPanel row={row} />}
      />
    </FullPageCard>
  );
};

export default CronJobsTracker;
